import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet";
import { useLocation } from 'react-router-dom';
import * as Steps from './steps/index';
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import {
  setBasicInfo,
  getSessionStorageItem,
  SESSION_STORAGE_CUSTOMIZED_TERM_KEY,
  SESSION_STORAGE_ACCOUNT_KEY,
  getBasicInfo,
  SESSION_STORAGE_BASIC_INFO_KEY, SESSION_STORAGE_TERM_SORT_FILTER_KEY,
  SESSION_STORAGE_CUSTOMIZED_HEALTH_KEY, SESSION_STORAGE_CUSTOMIZED_TEASER_KEY
} from '../../../sessionStorage';
import TermData from '../../Helpers/Data/Products/Term/TermData';
import { saveSessionEvent } from "../../../session";
import CheckSSOLogin from '../Common/CheckSSOLogin';
import '../Quoter.scss';
import './CustomizeTerm.scss';
import { format } from 'date-fns';
import TobaccoUsage from "../Common/Helpers/TobaccoUsage";
import { BestFeatures, EasySteps } from "./Helpers/Constant";
import { GetHandoffPath } from "../Common/GetHandoffPath";
import GetProductDetail from '../../Common/GetProductDetail';

export default function CustomizeTerm(props) {

  useEffect(() => {
    saveSessionEvent();
  }, [props.location]);

  const path = props.location ? props.location.pathname.split("/").pop() : null;
  const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_HEALTH_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);
  }

  // 27514 - Since E*Trade is unable to change the URL on their side, redirect to their landing page when they hit /term-insurance
  useEffect(() => {
    const productDetail = GetProductDetail(['/landing/term-insurance', '/term-insurance']);
    if (productDetail === undefined) {
      props.history.push('/');
    }
    if (props.location.pathname === '/term-insurance' && account.account.accountName.toLowerCase().indexOf("etrade") !== -1) {
      props.history.push('/landing/etrade-term');
    }
  }, []);

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
  const sessionStorageTermSettings = getSessionStorageItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);
  const teaserRateObject = getSessionStorageItem(SESSION_STORAGE_CUSTOMIZED_TEASER_KEY) || {};

  const prefill = props.location.search && props.location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();
  const teaserInfo = {
    faceAmount: (basicInfo && basicInfo.faceAmount) || (teaserRateObject.params && teaserRateObject.params.faceAmount),
    termLength: teaserRateObject.params && teaserRateObject.params.termLength,
    healthClass: teaserRateObject.params && teaserRateObject.params.healthClass,
  };

  const newFormState = {
    currentStep: 1,
    pages: {
      preQuote: Steps.PreQuote.DefaultFormState(prefill, teaserInfo, basicInfo),
      quoter: Steps.Quoter.DefaultFormState(basicInfo),
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  if (sessionStorageTermSettings) {
    initialFormState.pages.quoter.coverageAmount = sessionStorageTermSettings.term.coverageAmount;
    initialFormState.pages.quoter.coverageLength = sessionStorageTermSettings.term.termLength;
    initialFormState.pages.quoter.healthClass = sessionStorageTermSettings.term.healthClass;
  }
  
  const [formState, setFormState] = useState(initialFormState);
  const termData = TermData();
  const [multiTermResult, setMultiTermResult] = useState(false);
  const [isSSOAuthenticated, setIsSSOAuthenticated] = useState(false);
  
  // When coming from the landing page form, skip to step 2
  const location = useLocation();
  useEffect(() => {
   
    if (location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
    if (!isSSOAuthenticated) {
      CheckSSOLogin().then((json) => {
        setIsSSOAuthenticated(json);
      });
    }
  }, []);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        window.location = '/term-insurance';
      }
    }

    if (!nextStep) {
      return;
    }
    const newFullFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFullFormState);
  }, [path]);

  const setEmailPrefill = (basicInfo) => {
    setBasicInfo(basicInfo);
  };
  
  const setQuoteEmail = () => {
    const basicInfo = getBasicInfo();
    onFieldChange("selectAppointment", "email", basicInfo.email);
  };

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFullFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFullFormState);
    sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(newFullFormState));

    props.history.push(`/term-insurance/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const onBack = () => {
    let backStep = steps.find(s => s.step === formState.currentStep - 1);
    if (formState.currentStep === 3) {backStep = 1;} // Select appointment should go back to prequote

    if (!backStep) {
       props.history.push(`/landing/term-insurance`);
       return;
    }  

    const newFullFormState = { ...formState, currentStep: backStep.step };

    setFormState(newFullFormState);
    sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(newFullFormState));
    props.history.push(`/term-insurance/${backStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const logFinalDigitalQuote = (email, firstName, lastName, quoter, oneClickTypeId, referralId = null) => {
    const isSelectedQuoteCarrier = quoter.selectedQuote.carrierName !== "" && quoter.selectedQuote.carrierName !== null;

    return new Promise(resolve => {
      //save quote log for bms
      //keep in mind that this needs to match 1click quote log format
      const requestString = JSON.stringify({
        CoverageAmount: quoter.coverageAmount,
        CoverageLength: quoter.term,
        State: basicInfo.state,
        BirthDate: format(new Date(basicInfo.birthdate), 'yyyy-MM-dd'),
        HealthClass: quoter.healthClass,
        Gender: basicInfo.gender,
        Tobacco: TobaccoUsage(basicInfo.tobacco),
        Email: email,
        FirstName: firstName,
        LastName: lastName,
        selectedProductID: isSelectedQuoteCarrier && quoter.selectedQuote.carrierName.toLowerCase().includes("lga") ? quoter.selectedQuote.productID : "",
      });

      let jsonResponse;

      // set custom quote id in response if present
      if (referralId) {
        const response = { ...quoter.selectedQuote };
        response.PartnerQuoteId = referralId;

        jsonResponse = JSON.stringify(response);
      } else {
        jsonResponse = JSON.stringify(quoter.selectedQuote);
      }
      const pathName = GetHandoffPath(quoter.selectedQuote.productID);
      saveSessionEvent(true, pathName);
      //clear product session

      fetch(`/api/QuoteLog/ArchiveQuoteLog`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'SessionId': account.sessionId
        },
        body: JSON.stringify({
          QuoteLogId: quoter.selectedQuote.quoteLogId,
          OneClickTypeID: isSelectedQuoteCarrier && quoter.selectedQuote.carrierName.toLowerCase().includes("lga") ? 52 : oneClickTypeId, //digital oneclick type
          Request: requestString,
          Response: jsonResponse,
          ApplicationID: account.sessionId,
          OneClickAccountID: account.account.oneClickAccountId,
          SessionID: account.sessionId,
          ConsumerAccountId: account.account.accountId,
          PartnerQuoteId: referralId,
        }),
      }).then(() => {
        sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
        resolve();
      });
    });
  };

  const onNextBestow = () => {
    props.history.push(`/term-insurance/bestow`);

    window.scroll(0, 0);
  };

  const onNextHaven = () => {
    props.history.push(`/term-insurance/haven`);

    window.scroll(0, 0);
  };

  const onNextLga = () => {
    props.history.push(`/term-insurance/lga`);

    window.scroll(0, 0);
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  const steps = [
    {
      step: 1,
      path: 'prequote',
      progressTitle: 'Quick Quote',
      title: "TERM LIFE INSURANCE  /  CUSTOMIZE YOUR COVERAGE",
      component: (
        <Steps.PreQuote
          formState={formState.pages.preQuote}
          teaserRate={teaserRateObject.rate || undefined}
          fullFormState={formState}
          basicInfo={basicInfo}
          quoter={formState.pages.quoter}
          history={props.history}
          term30EnableTeaserRate={account.term30EnableTeaserRate}
          onFieldChange={(field, value) => onFieldChange('preQuote', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                quoter: {
                  ...formState.pages.quoter,
                  coverageAmount: coverageAmount,
                  term: termLength,
                  healthClass: healthClass
                }
              },
            })
          }
          onMultiFieldChange={(fs) => {
            setFormState(fs);
            sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(fs));
          }}
          onNext={onNext}
          onBack={onBack}
          onMultiTerm={(multi) => setMultiTermResult(multi)}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 2,
      path: 'quote-results',
      progressTitle: 'Getting Started',
      component: <Steps.Quoter
        productType={"Term3"}
        formState={formState.pages.quoter}
        fullFormState={formState}
        preQuoteDetails={formState.pages.preQuote}
        basicInfo={basicInfo}
        onNextBestow={onNextBestow}
        onNextHaven={onNextHaven}
        onNextLga={onNextLga}
        onNext={onNext}
        onBack={onBack}
        onFieldChange={(field, value) => onFieldChange('quoter', field, value)}
        setFormState={(coverageAmount, termLength, healthClass) =>
          setFormState({
            ...formState,
            pages: {
              ...formState.pages,
              quoter: {
                ...formState.pages.quoter,
                coverageAmount: coverageAmount,
                term: termLength,
                healthClass: healthClass
              }
            },
          })
        }
        onMultiFieldChange={(fs) => {
          setFormState(fs);
          sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(fs));
        }}
        setEmailPrefill={setEmailPrefill}
        setQuoteEmail={setQuoteEmail}
        coverageAmountSpecs={termData.specs.coverageAmount}
        history={props.history}
        multiTermResult={multiTermResult}
      />,
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 3,
      path: 'select-appointment',
      progressTitle: 'Schedule your application',
      title: "TERM LIFE INSURANCE  /  SCHEDULE CALL",
      component: (
        <Steps.SelectAppointment
          formState={formState.pages.selectAppointment}
          quoter={formState.pages.quoter}
          data={BestFeatures}
          title="Your Plan's Best Features"
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('selectAppointment', field, value)}
          onNext={onNext}
          history={props.history}
          setMultiField={(firstName, lastName, email) => {
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                appointment: { ...formState.pages.appointment, firstName: firstName, lastName: lastName, email: email }
              }
            });
            sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 4,
      path: 'thank-you',
      progressTitle: 'Schedule your application',
      title: "TERM LIFE INSURANCE  /  CALL SCHEDULED",
      component: (
        <Steps.ScheduleAppointment
          quoter={formState.pages.quoter}
          appointment={formState.pages.selectAppointment}
          data={EasySteps}
          title='What happens next'
          history={props.history}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 5,
      path: 'bestow',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Bestow
        formData={formState.pages}
        basicInfo={basicInfo}
        location={props.location}
        history={props.history}
        onBack={() => {
          if (props.history) {
            props.history.goBack();
          }
        }}
        logQuote={logFinalDigitalQuote}
        showCustomizeTermModal={true}
      />,
      hideProgressTracker: true,
      hidden: false,
    },
    {
      step: 6,
      path: 'haven',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Haven
        formData={formState.pages}
        basicInfo={basicInfo}
        enablePhone={true}
        onBack={() => {
          if (props.history) {
            props.history.goBack();
          }
        }}
        logQuote={logFinalDigitalQuote}
        showCustomizeTermModal={true}
      />,
      hideProgressTracker: true,
      hidden: false,
    },
    {
      step: 7,
      path: 'lga',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Lga
        formData={formState.pages}
        basicInfo={basicInfo}
        location={props.location}
        history={props.history}
        showCustomizeTermModal={true}
        onBack={() => {
          if (props.history) {
            props.history.goBack();
          }
        }}
        logQuote={logFinalDigitalQuote}
      />,
      hideProgressTracker: true,
      hidden: false,
    },
  ];

  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max.apply(Math, categories.map(function (o) {
    return o.categoryStep;
  }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);

  return (
    <div>
      <Helmet>
        <title>Term life insurance - {currentStepObj.progressTitle} </title>
      </Helmet>

      <HeaderProgressTracker showCustomizeProgressTracker={true} title={currentStepObj.title}
                            hideMainTitle={!currentStepObj.title}
                             hidden={currentStepObj.hidden} customClassNames={"customize-term"}
                             showContainer={currentStepObj.showContainer}
                             hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true}
                             stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3}
                             whiteBg={currentStepObj.whiteBackground} percent={percent}
                             fullWidth={currentStepObj.fullWidth}>
        <div className='CustomizeTerm pb-3'>
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}

CustomizeTerm.propTypes = {
  location: PropTypes.object,
  history : PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/CustomizeTerm/CustomizeTerm.js